// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import setFulfillmentMethod from "@insite/client-framework/Store/Context/Handlers/SetFulfillmentMethod";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import Radio, { RadioComponentProps, RadioStyle } from "@insite/mobius/Radio";
import RadioGroup, { RadioGroupComponentProps } from "@insite/mobius/RadioGroup";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import FieldSetPresentationProps, { FieldSetGroupPresentationProps } from "@insite/mobius/utilities/fieldSetProps";
import { StyledBox } from "@pace/XDL/Utilities";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {}

const mapStateToProps = (state: ApplicationState) => {
    const location = getLocation(state);
    const cartId = parseQueryString<{ cartId?: string }>(location.search).cartId;

    return {
        fulfillmentMethod: state.context.session.fulfillmentMethod,
        enableWarehousePickup: getSettingsCollection(state).accountSettings.enableWarehousePickup,
        cart: cartId ? getCartState(state, cartId) : getCurrentCartState(state),
    };
};

const mapDispatchToProps = {
    setFulfillmentMethod,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface CheckoutShippingFulfillmentMethodSelectorStyles {
    container?: GridContainerProps;
    gridItem?: GridItemProps;
    headingText?: TypographyPresentationProps;
    radioGroup?: FieldSetGroupPresentationProps<RadioGroupComponentProps>;
    shipRadio?: FieldSetPresentationProps<RadioComponentProps>;
    pickUpRadio?: FieldSetPresentationProps<RadioComponentProps>;
}

export const checkoutShippingFulfillmentMethodStyles: CheckoutShippingFulfillmentMethodSelectorStyles = {
    gridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
    headingText: { variant: "h5" },
    radioGroup: {
        css: css`
            display: block;
            ${RadioStyle} {
                display: inline-block;
                margin-right: 1em;
                & + ${RadioStyle} {
                    margin-top: 0;
                }
            }
        `,
    },
};

const styles = checkoutShippingFulfillmentMethodStyles;

const CheckoutShippingFulfillmentMethodSelector = ({
    fulfillmentMethod,
    enableWarehousePickup,
    setFulfillmentMethod,
    // Xng-Change
    cart,
}: Props) => {
    const [isUpdatingFulfillmentMethod, setIsUpdatingFulfillmentMethod] = useState(false);
    // Xng-Change PAC-1201
    const [firstTimeCalled, setFirstTimeCalled] = useState(false);

    if (!fulfillmentMethod || !enableWarehousePickup) {
        return null;
    }

    /**
     * Xng-Change PAC-1201
     * on the first load setFulfillmentMethod with the quote Id
     */
    useEffect(() => {
        if(!cart.value || !fulfillmentMethod || firstTimeCalled) {
            return;
        }
        setFirstTimeCalled(true);
        setFulfillmentMethod({
            fulfillmentMethod,
            quoteId: cart.value?.type === "Quote" ? cart?.id : undefined,
        } as any);
        window?.localStorage?.setItem("sessionFulfillmentMethod", fulfillmentMethod);
    }, [cart.value]);

    /**
     * Xng-Change PAC-1201
     * parse quoteId to the session fulfillment method
     * set sessionFulfillmentMethod on the localStorage to read it on loadCurrentCart handler
     */
    const handleChangeFulfillmentMethod = (event: React.ChangeEvent<HTMLInputElement>) => {
        setIsUpdatingFulfillmentMethod(true);
        setFulfillmentMethod({
            fulfillmentMethod: event.currentTarget.value as "Ship" | "PickUp",
            quoteId: cart.value?.type === "Quote" ? cart?.id : undefined,
            onComplete: () => {
                setIsUpdatingFulfillmentMethod(false);
            },
        } as any);
        window?.localStorage?.setItem("sessionFulfillmentMethod", event.currentTarget.value);
    };

    // XNG-Changes: structure
    return (
        <StyledBox {...styles.container}>
            <Typography {...styles.headingText} as="h2">
                {translate("Fulfillment Method")}
            </Typography>
            <RadioGroup
                {...styles.radioGroup}
                value={fulfillmentMethod}
                onChangeHandler={handleChangeFulfillmentMethod}
            >
                <Radio
                    {...styles.shipRadio}
                    value={FulfillmentMethod.Ship}
                    disabled={isUpdatingFulfillmentMethod}
                    data-test-selector="fulfillmentMethod_ship"
                >
                    {translate("Delivery")}
                </Radio>
                <Radio
                    {...styles.pickUpRadio}
                    value={FulfillmentMethod.PickUp}
                    disabled={isUpdatingFulfillmentMethod}
                    data-test-selector="fulfillmentMethod_pickUp"
                >
                    {translate("Will Call")}
                </Radio>
            </RadioGroup>
        </StyledBox>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CheckoutShippingFulfillmentMethodSelector),
    definition: {
        group: "Checkout - Shipping",
        displayName: "Fulfillment Method Selector",
        allowedContexts: ["CheckoutShippingPage"],
    },
};

export default widgetModule;
